import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import RosterTable from '../../components/roster/RosterTable';
import SearchBar from '../.././components/roster/SearchBar';
import MissingPhotosAlert from '../../components/roster/MissingPhotosAlert';
import CSVUpload from '../../components/roster/CSVUpload';
import { Artist } from '../../components/roster/types';
import { artistsApi } from '../../repositories/artists';
import { soundchartsEndpoints } from '../../repositories/soundcharts';
import Swal from 'sweetalert2';
import Pagination from '../../components/common/Pagination';
import PageHeader from '../../components/ui/PageHeader';
import { HOME_CRUMB } from '../../components/ui/Breadcrumbs';
import Button from '../../components/ui/Button';
import { confirmDanger } from '../../utils/swal';
import { useDebounce } from 'use-debounce';
import { SkeletonTableRows } from '../../components/ui/skeleton';
import { useIsTourRunning } from '../../context/TourContext';

const Roster: React.FC = () => {
  const [artists, setArtists] = useState<Artist[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, 400);
  const [showMissingPhotosAlert, setShowMissingPhotosAlert] = useState(false);
  const [showCSVUpload, setShowCSVUpload] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [missingPhotoArtists, setMissingPhotoArtists] = useState<Artist[]>([]);
  const [lastMissingPhotosCount, setLastMissingPhotosCount] = useState(0);
  const isTourRunning = useIsTourRunning();

  useEffect(() => {
    loadArtists();
  }, [page, limit, debouncedSearchTerm]);

  const loadArtists = async () => {
    try {
      setLoading(true);
      const response = await artistsApi.getAll({ page, limit, search: debouncedSearchTerm });
      const artistsData = response.data.data?.artists 
    
      // Fetch popularity for each artist
      const artistsWithPopularity = await Promise.all(
        artistsData.map(async (artist: Artist) => {
          if (artist.artistUUID) {
            try {
              const popularityResponse = await soundchartsEndpoints.getPopularityByArtist(
                artist.artistUUID,
                'spotify'
              );
              
              // Get the last element from the items array (latest popularity)
              const items = popularityResponse.data?.items || [];
              const latestPopularity = items.length > 0 ? items[items.length - 1] : null;
              
              return {
                ...artist,
                popularity: {
                  spotify: latestPopularity?.value ?? null
                }
              };
            } catch (error) {
              console.error(`Error fetching popularity for artist ${artist.artistName}:`, error);
              return { ...artist, popularity: { spotify: null } };
            }
          }

          return { ...artist, popularity: { spotify: null } };
        })
      );

      setArtists(artistsWithPopularity);

      // If your backend returns pagination info:
      if (response.data.data?.pagination) {
        setTotalPages(response.data.data.pagination.totalPages);
        setTotalResults(response.data.data.pagination.totalResults || 0);
      }

      // Fetch missing photo artists after loading main list
      try {
        const missingPhotoRes = await artistsApi.getArtistsMissingPhotos();
        const missing = missingPhotoRes.data.data || [];
        setMissingPhotoArtists(missing);

        // Smart alert logic
        const dismissed = localStorage.getItem('missingPhotosAlertDismissed');
        const dismissedCount = localStorage.getItem('dismissedMissingPhotosCount');
        const snoozedForSession = sessionStorage.getItem('missingPhotosAlertSnoozed');
        const currentCount = missing.length;

        if (currentCount > 0 && snoozedForSession !== 'true') {
          if (dismissed !== 'true') {
            // Never dismissed - always show
            setShowMissingPhotosAlert(true);
          } else {
            // Was dismissed, check if new artists were added
            const previousCount = dismissedCount ? parseInt(dismissedCount, 10) : 0;
            if (currentCount > previousCount) {
              setShowMissingPhotosAlert(true);
            }
          }
        } else {
          setShowMissingPhotosAlert(false);
        }
      } catch (error) {
        console.error('Error fetching missing photos:', error);
        setShowMissingPhotosAlert(false);
      }
    } catch (error: any) {
      console.error('Error loading artists:', error);
      Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || error.response?.data?.error || 'Failed to load artists. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCSVUploadSuccess = () => {
    setShowCSVUpload(false);
    loadArtists();
  };

  const downloadCSVTemplate = () => {

    const headers = [
      'Artist Name*',
      'Spotify URI*',
      "Apple Artist ID*",
      'ASIN*',
      'Photo',
      'Bio',
      'Artist Hometown',
      'Artist Current Town',
      'Country',
      'Label*',
      'Distributor*',
      'Instagram*',
      'TikTok',
      'X',
      'Facebook',
      'Snapchat',
      'SoundCloud URL*',
      'TIDAL URL*',
      'Spotify URL',
      'Apple URL',
      'Amazon URL',
      'Deezer URL',
      'Lyric Language',
      'Location of Listeners*',
      'SoundCloud Contact*',
      'Vevo Rep*',
      'Similar Artists*'
    ];

    // Three example rows. Row 1 deliberately uses fields with internal commas
    // (wrapped in quotes) to confirm the parser handles them; Rows 2 and 3 use
    // unique Spotify URIs so the import won't collide with existing test data.
    const exampleRows = [
      [
        'QA Test Alpha',
        '7dGJo4pcD2V6oG8kP0tJRR',
        '200000001',
        'B0TEST0001',
        'https://example.com/alpha.jpg',
        '"Multi-genre artist, producer, and songwriter"',
        '"Brooklyn, NY"',
        '"Los Angeles, CA"',
        'USA',
        'Alpha Records',
        'Alpha Distribution',
        'https://instagram.com/qa_alpha',
        'https://tiktok.com/@qa_alpha',
        'https://x.com/qa_alpha',
        'https://facebook.com/qa_alpha',
        'https://snapchat.com/add/qa_alpha',
        'https://soundcloud.com/qa_alpha',
        'https://tidal.com/qa_alpha',
        'https://open.spotify.com/artist/qa_alpha',
        'https://music.apple.com/artist/qa_alpha',
        'https://amazon.com/artist/qa_alpha',
        'https://deezer.com/artist/qa_alpha',
        'English',
        'United States',
        'Example SoundCloud Contact',
        'Example Vevo Rep',
        '"Similar Artist 1, Similar Artist 2, Similar Artist 3"'
      ],
      [
        'QA Test Beta',
        '4nDoRrQiYLoBzwC5BhVJzF',
        '200000002',
        'B0TEST0002',
        '',
        '"Indie folk singer, writer, performer"',
        '"Austin, TX"',
        '"Nashville, TN"',
        'USA',
        'Beta Label',
        'Self Distributed',
        'https://instagram.com/qa_beta',
        '',
        '',
        '',
        '',
        'https://soundcloud.com/qa_beta',
        'https://tidal.com/qa_beta',
        '',
        '',
        '',
        '',
        'English',
        'United States',
        'Example SoundCloud Contact',
        'Example Vevo Rep',
        '"Similar Artist 1, Similar Artist 2, Similar Artist 3"'
      ],
      [
        'QA Test Gamma',
        '2CIMQHirSU0MQqyYHq0eOx',
        '200000003',
        'B0TEST0003',
        '',
        '"Electronic producer, DJ"',
        'Berlin',
        'Berlin',
        'Germany',
        'Gamma Records',
        'Gamma Distribution',
        'https://instagram.com/qa_gamma',
        'https://tiktok.com/@qa_gamma',
        '',
        '',
        '',
        'https://soundcloud.com/qa_gamma',
        'https://tidal.com/qa_gamma',
        '',
        '',
        '',
        '',
        'English',
        'United States',
        'Example SoundCloud Contact',
        'Example Vevo Rep',
        '"Similar Artist 1, Similar Artist 2, Similar Artist 3"'
      ]
    ];

    // Create CSV content
    const csvLines = [
      headers.join(','),
      ...exampleRows.map(row => row.join(','))
    ];
    const csvContent = csvLines.join('\n');

    // Add BOM for proper Excel encoding
    const BOM = '\uFEFF';
    const csvWithBOM = BOM + csvContent;

    // Trigger download
    const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'artist_roster_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    // Show success message
    Swal.fire({
      title: 'Template Downloaded',
      text: 'CSV template has been downloaded to your computer.',
      icon: 'success',
      confirmButtonText: 'OK'
    });
  };

  const handleMissingPhotosDismiss = (permanent: boolean = false) => {
    setShowMissingPhotosAlert(false);

    if (permanent) {
      // Store the current count when permanently dismissed
      localStorage.setItem('missingPhotosAlertDismissed', 'true');
      localStorage.setItem('dismissedMissingPhotosCount', missingPhotoArtists.length.toString());

      Swal.fire({
        title: 'Alert Dismissed',
        text: `Alert dismissed. You'll be notified again if more than ${missingPhotoArtists.length} artists are missing photos.`,
        icon: 'info',
        confirmButtonText: 'OK'
      });
    } else {
      // Remind Later - snooze for the current session only (resets on next login)
      sessionStorage.setItem('missingPhotosAlertSnoozed', 'true');
    }
  };

  const handleViewMissingPhotos = () => {
    // You can implement navigation to a filtered view or settings
    // For now, just close the alert
    setShowMissingPhotosAlert(false);

    // Optionally show a message
    Swal.fire({
      title: 'Missing Photos',
      text: `You have ${missingPhotoArtists.length} artists without photos. Consider adding photos to improve your roster presentation.`,
      icon: 'info',
      confirmButtonText: 'OK'
    });
  };

  const handleDeleteArtist = async (id: string) => {
    const result = await confirmDanger({
      title: 'Delete Artist?',
      text: 'Are you sure you want to delete this artist? This action cannot be undone.',
      confirmButtonText: 'Yes, Delete',
    });

    if (result.isConfirmed) {
      try {
        await artistsApi.delete(id);
        Swal.fire('Deleted!', 'Artist has been deleted.', 'success');
        loadArtists(); // Refresh the list
      } catch (error: any) {
        Swal.fire('Error', error.response?.data?.message || 'Failed to delete artist.', 'error');
      }
    }
  };
  // Load the last count on component mount
  useEffect(() => {
    const savedCount = localStorage.getItem('lastMissingPhotosCount');
    if (savedCount) {
      setLastMissingPhotosCount(parseInt(savedCount, 10));
    }
  }, []);
  // Same container as the other revamped pages: the shell supplies the neutral
  // page background, so this must not tint it.
  return (
    // Same container as /past-submissions and /email. This page and /email/logs
    // were the only two capped at max-w-7xl, which left a wide empty gutter
    // beside the table while every other page ran full width.
    <div className="dashboard-container py-2 sm:p-6">
      <PageHeader
        breadcrumbs={[HOME_CRUMB, { label: "Roster" }]}
        className="mb-6"
        title="Artist Roster"
        actions={
              <div className="flex flex-wrap items-center gap-3" data-tour="csv-buttons-area">
                {/* Same hierarchy as /dashboard: one primary action, the rest
                    supporting. */}
                <Button
                  variant="link"
                  size="inline"
                  onClick={downloadCSVTemplate}
                  data-tour="download-csv-btn"
                >
                  Download CSV Template
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setShowCSVUpload(true)}
                  data-tour="import-csv-btn"
                >
                  Import CSV
                </Button>
                <Button asChild>
                  <Link to="/roster/add-artist" data-tour="add-artist-btn">
                    Add Artist
                  </Link>
                </Button>
              </div>
        }
      />

      {/* One level of padding only - the page container above supplies it. */}
      <SearchBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
      />

      {loading ? (
        <div className="overflow-hidden bg-white border rounded-lg rounded-t-none border-neutral-200" aria-busy="true">
          <table className="min-w-full">
            <tbody>
              <SkeletonTableRows rows={8} columns={5} />
            </tbody>
          </table>
        </div>
      ) : (
        <>
          <RosterTable artists={artists} onDelete={handleDeleteArtist} />

          <div className="mt-3">
            {/* totalItems/limit give the "Showing 1 to 10 of 42" count line that
                every other paginated page has - totalResults was already being
                fetched, it just was not being passed down. */}
            <Pagination
              page={page}
              totalPages={totalPages}
              setPage={setPage}
              totalItems={totalResults}
              limit={limit}
            />
          </div>
        </>
      )}

      {/* Held back while the product tour runs: its backdrop would stack on
          the tour's overlay and dim the highlighted element. It shows once the
          tour is finished or minimized. */}
      {showMissingPhotosAlert && !isTourRunning && (
        <MissingPhotosAlert
          artists={missingPhotoArtists}
          onDismiss={handleMissingPhotosDismiss}
          onViewArtists={handleViewMissingPhotos}
        />
      )}

      {showCSVUpload && (
        <CSVUpload
          onClose={() => setShowCSVUpload(false)}
          onSuccess={handleCSVUploadSuccess}
        />
      )}
    </div>
  );
};

export default Roster;